import {
  apiAddAppConfig,
  apiGetAppConfigs,
  apiUpdateAppConfig,
} from "@lib/api/settings";
import { APP_VERSION } from "@lib/constants/AppConst";
import { useTypedSelector } from "@lib/redux";
import { AuthRoles } from "@lib/types";
import { AppConfigAppVersion, AppConfigNames } from "@lib/types/AppConfig";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { toast } from "react-toastify";

export default function AppVersionProvider() {
  const { isAuthenticated, user } = useTypedSelector((state) => state.auth);
  const isAdmin = user?.role === AuthRoles.Admin;

  const { data, isLoading } = useQuery({
    queryKey: ["app-version"],
    queryFn: () =>
      apiGetAppConfigs({
        filter: [`name||eq||${AppConfigNames.appVersionConfig}`],
      }),
    refetchOnWindowFocus: true,
    refetchInterval: 1000 * 60 * 5, // 5 dakika
    enabled: isAuthenticated,
  });

  const apiData = data?.data?.data?.[0] || null;
  const appVersion = apiData?.data as AppConfigAppVersion | null;

  const { mutateAsync: updateAppConfig } = useMutation({
    mutationFn: apiUpdateAppConfig,
    mutationKey: ["app-version"],
  });

  const { mutateAsync: addAppConfig } = useMutation({
    mutationFn: apiAddAppConfig,
    mutationKey: ["app-version"],
  });

  const compareVersions = (a: string, b: string): number => {
    const [aMaj, aMin, aPatch] = a.split(".").map(Number);
    const [bMaj, bMin, bPatch] = b.split(".").map(Number);

    if (aMaj !== bMaj) return aMaj - bMaj;
    if (aMin !== bMin) return aMin - bMin;
    return aPatch - bPatch;
  };

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) return;

    if (!apiData) {
      if (isAdmin) {
        addAppConfig({
          enabled: true,
          name: AppConfigNames.appVersionConfig,
          data: { app: APP_VERSION },
        });
      }
      return;
    }

    if (!appVersion?.app) return;

    const diff = compareVersions(APP_VERSION, appVersion.app);

    if (diff > 0 && isAdmin) {
      updateAppConfig({
        id: apiData.id,
        data: { app: APP_VERSION },
      });
    }

    if (diff < 0) {
      toast.error(<FormattedMessage id="common.newVersionAvailable" />, {
        onClick: () => window.location.reload(),
        autoClose: false,
        closeOnClick: true,
        progress: undefined,
      });
    }
  }, [isLoading, isAuthenticated, appVersion]);

  return null;
}
